import React, { useState, useEffect, useRef } from "react";

import { Grid, ListItem, ListItemText, Collapse, IconButton } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { AiFillStar, AiOutlineStar } from "react-icons/ai";
import { useHistory } from "react-router-dom";

import { PRIVATE_ROUTES } from "../../../../../constants";
import { useAppState } from "../../../../../context/StateContext";
import { Checklist } from "../../../../../models/checklist";
import { Report } from "../../../../../models/report";
import { ReportGroup } from "../../../../../models/reports.groups";
import { ReportGroup as EReportGroup, ReportName, ResourceName } from "../../../../../util/enums";
import env from "../../../../../util/env";
import { includeInSearch } from "../../../../../util/includeInSearch";
import useStyles from "./styles";
import { GenerateReportDrawer, GenerateReportDrawerHandles } from "Components/GenerateReport";
import { RolePermissionsResponse } from "Models/resources";

interface Props {
  reportGroup: ReportGroup;
  searchValue?: string;
  addToFavorites?: (reportId?: number, checklistId?: number) => void;
  removeFromFavorites?: (reportId?: number, checklistId?: number) => void;
  checklists?: Checklist[];
  favoriteReports: Report[];
  favoriteChecklists: Checklist[];
  reportsToView?: RolePermissionsResponse[];
}

const ReportListItem = ({
  reportGroup,
  searchValue,
  addToFavorites,
  removeFromFavorites,
  checklists,
  favoriteReports,
  favoriteChecklists,
  reportsToView,
}: Props) => {
  const classes = useStyles();
  const { currentUser } = useAppState();
  const [openReport, setOpenReport] = useState<boolean>(false);
  const accessEmails = ["peter.smith@cottonon.com.au", "adrian.ficorilli@shipeezi.com"];
  const history = useHistory();

  const generateReportRef = useRef<GenerateReportDrawerHandles>(null);

  useEffect(() => {
    if (searchValue) {
      setOpenReport(true);
    }
  }, [searchValue]);

  const isReportOrChecklistFavorite = (name: string, reportId?: number, checklistId?: number) => {
    const favoritedReport = reportId
      ? favoriteReports?.find((favoriteReport) => reportId === favoriteReport.id)
      : null;

    if (favoritedReport) return true;

    const favoridedChecklist = checklistId
      ? favoriteChecklists.find((favoriteChecklist) => checklistId === favoriteChecklist.id)
      : null;

    if (favoridedChecklist) return true;

    return false;
  };

  const checkIfReportExists = (reports: any[], reportsToView: RolePermissionsResponse[]) => {
    if (!reportsToView || reportsToView?.length === 0) return reports;
    return reports
      ?.map((report) => {
        const existsInReportsToView = reportsToView?.some(
          (obj) => obj?.objectDetails?.id === report?.id
        );
        if (existsInReportsToView) {
          return report;
        }
      })
      ?.filter((report) => !!report);
  };

  const renderReportChecklist = (name: string, checklistId?: number, reportId?: number) => {
    const isWorkOrderReport = name.toLowerCase() === "work order";
    const reportKey = checklistId || reportId;

    const handleOpenReport = () => {
      if (isWorkOrderReport) {
        generateReportRef?.current?.open();
        return;
      }

      history.push(`${PRIVATE_ROUTES.ANALYTICS_MNT}`, {
        reportGroup,
        name,
        checklistId,
        reportId,
      });
    };

    const handleFavoriteClick = (
      event: React.MouseEvent<HTMLButtonElement>,
      action?: (reportId?: number, checklistId?: number) => void
    ) => {
      event.stopPropagation();
      action?.(reportId, checklistId);
    };

    return (
      <Grid key={reportKey} item xs={4} className={classes.reportWrapper}>
        <Grid
          container
          className={classes.itemReport}
          onClick={handleOpenReport}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleOpenReport();
            }
          }}
          role="button"
          tabIndex={0}
          data-cy={`reportCard${reportKey}`}
        >
          {isReportOrChecklistFavorite(name, reportId, checklistId) ? (
            <IconButton
              className={classes.favoriteIcon}
              onClick={(event) => handleFavoriteClick(event, removeFromFavorites)}
              data-cy={`favoriteReportRemove${reportKey}`}
            >
              <AiFillStar color="#FEC84B" size={24} />
            </IconButton>
          ) : (
            <IconButton
              className={classes.favoriteIcon}
              onClick={(event) => handleFavoriteClick(event, addToFavorites)}
              data-cy={`favoriteReportAdd${reportKey}`}
            >
              <AiOutlineStar color="#D0D5DD" size={24} />
            </IconButton>
          )}

          <Grid item className={classes.itemReportDescription}>
            <span className={classes.reportTitle}>{name}</span>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderReports = () => {
    if (reportGroup?.reports && reportGroup?.reports?.length) {
      const reports =
        reportGroup?.group?.toLowerCase() === ResourceName.MANAGEMENT?.toLowerCase()
          ? reportGroup?.reports
          : checkIfReportExists(reportGroup?.reports, reportsToView ?? []);
      if (!reports || reports?.length === 0) return;
      return reports?.map((report) => {
        if (
          report?.name === ReportName.SHIPMENT_COST_REPORT &&
          !accessEmails.includes(currentUser?.username || "") &&
          env.NODE_ENV === "production"
        )
          return null;

        if (report?.name && report?.description && includeInSearch(report?.name, searchValue)) {
          return renderReportChecklist(report?.name, undefined, report?.id);
        }

        return null;
      });
    }

    return null;
  };

  const renderChecklists = () => {
    if (checklists && checklists?.length && reportGroup?.group === EReportGroup.Operations) {
      const reportChecklists = checkIfReportExists(checklists, reportsToView ?? []);
      if (!reportChecklists || reportChecklists?.length === 0) return;
      return reportChecklists?.map((checklist) => {
        if (
          (checklist?.description || checklist?.code) &&
          checklist?.id &&
          includeInSearch(checklist?.description, searchValue)
        ) {
          return renderReportChecklist(
            (checklist?.description || checklist?.code) ?? "",
            checklist?.id,
            undefined
          );
        }

        return null;
      });
    }
  };

  const renderReportList = () => {
    return (
      <Grid container spacing={2} className={classes.reportFavoriteItems}>
        {renderReports()}
        {renderChecklists()}
      </Grid>
    );
  };

  switch (reportGroup?.group) {
    case EReportGroup.Management:
      return (
        <>
          {renderReportList()}
          <GenerateReportDrawer ref={generateReportRef} />
        </>
      );

    default:
      return (
        <>
          <ListItem
            button
            className={classes.reportDropdown}
            onClick={() => setOpenReport(!openReport)}
          >
            {openReport ? <ExpandLess /> : <ExpandMore />}
            <ListItemText className={classes.reportItem} primary={reportGroup.subGroup} />
          </ListItem>
          <Collapse
            unmountOnExit
            in={openReport}
            timeout="auto"
            className={classes.collapseContainer}
          >
            {renderReportList()}
          </Collapse>

          <GenerateReportDrawer ref={generateReportRef} />
        </>
      );
  }
};

export default ReportListItem;
